import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../contexts/ThemeSelection";
import useAccentColor, { contrastText } from "../hooks/useAccentColor";
import useBadges from "../hooks/useBadges";
import { useUser } from "../contexts/UserContext";
import { BadgeStatus, RARITY_COLORS, RARITY_LABELS } from "../types/badges";
import styles from "../styles/BadgeUnlockNotifier.module.css";

const STORAGE_KEY = "onyxlife_seen_badges";

function getSeenBadges(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* ignore */ }
  return new Set();
}

function markBadgeSeen(id: string) {
  const seen = getSeenBadges();
  seen.add(id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(seen)));
}

/** Seed localStorage with already-unlocked badges on first ever load. */
function seedSeenBadges(unlocked: BadgeStatus[]) {
  const seen = getSeenBadges();
  if (seen.size > 0) return; // already seeded
  const ids = unlocked.map((b) => b.id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

const PARTICLE_COUNT = 10;

const BadgeUnlockNotifier: React.FC = () => {
  const { theme, activeTheme } = useTheme();
  const { accent } = useAccentColor();
  const tc = theme[activeTheme];
  const { badges, loading } = useBadges();
  const userCtx = useUser();

  // Don't show badge popups until onboarding is complete
  const onboardingDone = !!userCtx?.userDetails?.onboarding;

  const [queue, setQueue] = useState<BadgeStatus[]>([]);
  const [current, setCurrent] = useState<BadgeStatus | null>(null);
  const [seeded, setSeeded] = useState(false);

  // Detect newly unlocked badges
  useEffect(() => {
    if (loading || !onboardingDone) return;

    const unlocked = badges.filter((b) => b.unlocked);

    // First time: seed localStorage so existing badges don't all pop up
    if (!seeded) {
      seedSeenBadges(unlocked);
      setSeeded(true);
      // After seeding, still check for genuinely new ones
    }

    const seen = getSeenBadges();
    const newBadges = unlocked.filter((b) => !seen.has(b.id));

    if (newBadges.length > 0) {
      // Mark all as seen immediately so we don't re-queue on re-render
      for (const b of newBadges) markBadgeSeen(b.id);
      setQueue((prev) => [...prev, ...newBadges]);
    }
  }, [badges, loading, seeded, onboardingDone]);

  // Show next from queue
  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue((prev) => prev.slice(1));
    }
  }, [current, queue]);

  const dismiss = useCallback(() => {
    setCurrent(null);
  }, []);

  const dismissAll = useCallback(() => {
    setCurrent(null);
    setQueue([]);
  }, []);

  // Particle positions (static random offsets)
  const particles = useMemo(() => {
    return Array.from({ length: PARTICLE_COUNT }, (_, i) => {
      const angle = (i / PARTICLE_COUNT) * 360;
      const rad = (angle * Math.PI) / 180;
      const dist = 60 + Math.random() * 40;
      return {
        dx: Math.cos(rad) * dist,
        dy: Math.sin(rad) * dist,
        delay: Math.random() * 0.3,
      };
    });
  }, []);

  if (!current) return null;

  const rarityColor = RARITY_COLORS[current.rarity];

  return (
    <div className={styles.backdrop} onClick={dismiss}>
      <div
        className={styles.toast}
        style={{ backgroundColor: tc.panelColor, color: tc.color }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Celebration particles */}
        <div className={styles.particles}>
          {particles.map((p, i) => (
            <div
              key={i}
              className={styles.particle}
              style={{
                left: "50%",
                top: "35%",
                backgroundColor: i % 2 === 0 ? rarityColor : accent,
                animationDelay: `${p.delay + 0.3}s`,
                // CSS custom properties for the particle direction
                "--dx": `${p.dx}px`,
                "--dy": `${p.dy}px`,
              } as React.CSSProperties}
            />
          ))}
        </div>

        <div className={styles.unlockLabel} style={{ color: rarityColor }}>
          Badge Unlocked!
        </div>

        <div
          className={styles.iconWrap}
          style={{
            backgroundColor: `${rarityColor}20`,
            color: rarityColor,
            borderColor: rarityColor,
          }}
        >
          <i className={current.icon} />
        </div>

        <div className={styles.title} style={{ color: tc.color }}>
          {current.title}
        </div>
        <div className={styles.description} style={{ color: tc.color }}>
          {current.description}
        </div>

        <div
          className={styles.rarityBadge}
          style={{ backgroundColor: `${rarityColor}18`, color: rarityColor }}
        >
          <i className="fa-solid fa-diamond" style={{ fontSize: "0.55rem" }} />
          {RARITY_LABELS[current.rarity]}
        </div>

        <button
          className={styles.dismissBtn}
          style={{ backgroundColor: accent, color: contrastText(accent) }}
          onClick={dismiss}
        >
          Nice!
        </button>

        {queue.length > 0 && (
          <>
            <div className={styles.queueIndicator} style={{ color: tc.color }}>
              +{queue.length} more badge{queue.length !== 1 ? "s" : ""}
            </div>
            <button
              className={styles.skipAllBtn}
              style={{ color: tc.color, opacity: 0.65 }}
              onClick={dismissAll}
            >
              Skip all
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BadgeUnlockNotifier;
